import keysMapping from './keys.mapping.json';
import { resolveDisplayCode } from '../services/KeyboardLayoutService';

// ---------------------------------------------------------------------------
// "keys" spritesheet — Kenney Input Prompts Pixel 16× sprites (CC0).
// The assets are a collection of individual PNGs in
// `public/sprites/ui/keys/tile_XXXX.png`, not a single atlas.
// ---------------------------------------------------------------------------

const KEYS_FOLDER = '/sprites/ui/keys';

/**
 * Format of an entry in `keys.mapping.json`:
 *   • `string`   → single tile (normal 16×16 key).
 *   • `string[]` → N tiles displayed side by side on a single row
 *                  (Space 3-wide, Tab/Shift/Alt/Enter/Backspace 2-wide…).
 * All paths are relative to `KEYS_FOLDER`.
 */
export type KeySpriteEntry = string | string[];

export const KEY_SPRITE_MAPPING = keysMapping as Record<string, KeySpriteEntry>;

export type KeySpriteVariant = 'light' | 'dark';

/**
 * Resolves a sprite id to its tile URLs. The id may be given with or without
 * the variant prefix:
 *   • `'keyboard.char_a'`       → `'light.keyboard.char_a'` (default variant)
 *   • `'light.keyboard.char_a'` → used as-is
 *   • `'dark.xbox.leftbumper'`  → explicit dark variant
 * Returns `null` if the id is absent from the mapping.
 */
export function getKeySpriteUrls(
  spriteId: string,
  variant: KeySpriteVariant = 'light',
): string[] | null {
  const id =
    spriteId.startsWith('light.') || spriteId.startsWith('dark.')
      ? spriteId
      : `${variant}.${spriteId}`;
  const entry = KEY_SPRITE_MAPPING[id];
  if (!entry) {
    return null;
  }
  const tiles = Array.isArray(entry) ? entry : [entry];
  return tiles.map((f) => `${KEYS_FOLDER}/${f}`);
}

/**
 * "Single tile" variant — returns the first tile's URL (useful for
 * preview / debug; for real rendering use `getKeySpriteUrls`).
 */
export function getKeySpriteUrl(
  spriteId: string,
  variant: KeySpriteVariant = 'light',
): string | null {
  const urls = getKeySpriteUrls(spriteId, variant);
  return urls ? urls[0] : null;
}

// ---------------------------------------------------------------------------
// Mapping KeyboardEvent.code → sprite id
//
// Follows the Kenney naming convention: `char_a..char_z`, `num_0..num_9`,
// `arrow_up/down/left/right`, `space`, `esc`, `enter_a`, `f1..f12`. Codes
// with no mapped sprite return `null` — the component then shows the text
// fallback label (`KEY_LABELS`).
// ---------------------------------------------------------------------------

const KEYBOARD_CODE_TO_SPRITE: Record<string, string> = {
  // Letters
  KeyA: 'keyboard.char_a',
  KeyB: 'keyboard.char_b',
  KeyC: 'keyboard.char_c',
  KeyD: 'keyboard.char_d',
  KeyE: 'keyboard.char_e',
  KeyF: 'keyboard.char_f',
  KeyG: 'keyboard.char_g',
  KeyH: 'keyboard.char_h',
  KeyI: 'keyboard.char_i',
  KeyJ: 'keyboard.char_j',
  KeyK: 'keyboard.char_k',
  KeyL: 'keyboard.char_l',
  KeyM: 'keyboard.char_m',
  KeyN: 'keyboard.char_n',
  KeyO: 'keyboard.char_o',
  KeyP: 'keyboard.char_p',
  KeyQ: 'keyboard.char_q',
  KeyR: 'keyboard.char_r',
  KeyS: 'keyboard.char_s',
  KeyT: 'keyboard.char_t',
  KeyU: 'keyboard.char_u',
  KeyV: 'keyboard.char_v',
  KeyW: 'keyboard.char_w',
  KeyX: 'keyboard.char_x',
  KeyY: 'keyboard.char_y',
  KeyZ: 'keyboard.char_z',
  // Digits
  Digit0: 'keyboard.num_0',
  Digit1: 'keyboard.num_1',
  Digit2: 'keyboard.num_2',
  Digit3: 'keyboard.num_3',
  Digit4: 'keyboard.num_4',
  Digit5: 'keyboard.num_5',
  Digit6: 'keyboard.num_6',
  Digit7: 'keyboard.num_7',
  Digit8: 'keyboard.num_8',
  Digit9: 'keyboard.num_9',
  // Function keys
  F1: 'keyboard.f1',
  F2: 'keyboard.f2',
  F3: 'keyboard.f3',
  F4: 'keyboard.f4',
  F5: 'keyboard.f5',
  F6: 'keyboard.f6',
  F7: 'keyboard.f7',
  F8: 'keyboard.f8',
  F9: 'keyboard.f9',
  F10: 'keyboard.f10',
  F11: 'keyboard.f11',
  F12: 'keyboard.f12',
  // Arrows
  ArrowUp: 'keyboard.arrow_up',
  ArrowDown: 'keyboard.arrow_down',
  ArrowLeft: 'keyboard.arrow_left',
  ArrowRight: 'keyboard.arrow_right',
  // Special keys (mostly multi-tile composites — cf. `keys.mapping.json`)
  Space: 'keyboard.space',
  Escape: 'keyboard.esc',
  Enter: 'keyboard.enter',
  Backspace: 'keyboard.backspace',
  Tab: 'keyboard.tab',
  ShiftLeft: 'keyboard.shift',
  ShiftRight: 'keyboard.shift',
  AltLeft: 'keyboard.alt',
  AltRight: 'keyboard.alt',
  // Ctrl not yet identified in the pack — text fallback "Ctrl".
};

/**
 * Returns the sprite id for a `KeyboardEvent.code`, or `null` if there is no
 * mapping (the caller must then show the text label).
 */
export function getKeyboardCodeSprite(code: string): string | null {
  const display = resolveDisplayCode(code);
  return KEYBOARD_CODE_TO_SPRITE[display] ?? null;
}

// ---------------------------------------------------------------------------
// Mapping gamepad button index → sprite id (default Xbox layout)
//
// Button order follows the W3C standard Gamepad spec: 0=physical B on Xbox,
// 1=A, 2=Y, 3=X, etc. `GAMEPAD_BUTTON_LABELS` uses the same convention.
// ---------------------------------------------------------------------------

const GAMEPAD_BUTTON_TO_SPRITE: Record<number, string> = {
  0: 'xbox.b',
  1: 'xbox.a',
  2: 'xbox.y',
  3: 'xbox.x',
  4: 'xbox.leftbumper',
  5: 'xbox.rightbumper',
  6: 'xbox.lefttrigger-short',
  7: 'xbox.righttrigger-short',
  8: 'xbox.view-select',
  9: 'xbox.menu-start',
  10: 'xbox.left_stick_click',
  11: 'xbox.right_stick_click',
  12: 'xbox.dpad_up',
  13: 'xbox.dpad_down',
  14: 'xbox.dpad_left',
  15: 'xbox.dpad_right',
};

export function getGamepadButtonSprite(index: number): string | null {
  return GAMEPAD_BUTTON_TO_SPRITE[index] ?? null;
}

// ---------------------------------------------------------------------------
// Mapping gamepad axis → sprite id (left/right sticks)
//
// W3C standard Gamepad convention: axes 0/1 = left stick X/Y, axes 2/3 =
// right stick X/Y. `positive` = right / down (screen), `negative` = left / up.
// ---------------------------------------------------------------------------

const GAMEPAD_AXIS_TO_SPRITE: Record<string, string> = {
  '0:negative': 'xbox.left_stick_left',
  '0:positive': 'xbox.left_stick_right',
  '1:negative': 'xbox.left_stick_up',
  '1:positive': 'xbox.left_stick_down',
  '2:negative': 'xbox.right_stick_left',
  '2:positive': 'xbox.right_stick_right',
  '3:negative': 'xbox.right_stick_up',
  '3:positive': 'xbox.right_stick_down',
};

/**
 * Resolves a directional axis (left/right stick) to its sprite. Returns
 * `null` if the axis has no mapping (beyond the 4 axes of the two standard
 * sticks).
 */
export function getGamepadAxisSprite(
  axisIndex: number,
  direction: 'positive' | 'negative',
): string | null {
  return GAMEPAD_AXIS_TO_SPRITE[`${axisIndex}:${direction}`] ?? null;
}
